import React, { useEffect, useMemo, useState } from 'react';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { canManagePromoCode, isPromoCodeVisible } from '@utils/Permissions/Capabilities';
import { Box, Card, InputAdornment, Stack, TextField, Typography , Tooltip } from '@mui/material';
import { DataGrid, esES, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import useOpenModal from '@hooks/useOpenModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import PromotionalCodeService from '@api/PromotionalCodes/PromotionalCodeCtrl';
import { PromotionalCode } from '@api/PromotionalCodes/PromotionalCode';
import { ModalSavePromotionalCode } from '@components/Admin/PromotionalCodes/Modals/ModalSavePromotionalCode';
import IOSSwitch from '@components/Utilities/Switch/IOSSwitch';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid';
import CustomButton from '@components/Utilities/Buttons/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import ReplayIcon from '@mui/icons-material/Replay';
import { setPromotionalCodes } from '@store/krispisStore';
import { History as HistoryIcon } from '@mui/icons-material';
import { setActivityDrawer } from '@store/appStore';
const ListPromotionalCodesScreen: React.FunctionComponent = () => {
    const { promotionalCodes } = useSelector((state: RootReducer) => state.krispis);
    const promotionalCodeService = useMemo(() => new PromotionalCodeService(), []);
    const [currentPromotionalCode, setCurrentPromotionalCode] = useState<PromotionalCode | undefined>(undefined);
    const modalSavePromotionalCode = useOpenModal();
    const [filters, setFilters] = useState({
        search: ''
    });
    const dataGridRef = useGridApiRef();
    const dispatch = useDispatch();

    const getData = async () => {
        showLoader();
        try {
            const promotionalCodesData = await promotionalCodeService.getPromotionalCodes();
            dispatch(setPromotionalCodes(promotionalCodesData));
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalSavePromotionalCode = () => {
        setCurrentPromotionalCode(undefined);
        modalSavePromotionalCode.toggle();
    };
    const onSavePromotionalCode = async (promotionalCode: PromotionalCode) => {
        showLoader();
        try {
            await promotionalCodeService.savePromotionalCode(promotionalCode);
            const promoCodes = [...promotionalCodes];
            const index = promoCodes.findIndex((p) => p.id === promotionalCode.id);
            if (index !== -1) {
                promoCodes[index] = {
                    ...promoCodes[index],
                    ...promotionalCode
                };
            } else {
                promoCodes.push({
                    ...promotionalCode,
                    usage: 0,
                    usageAmount: 0
                });
            }
            //await getData();
            dispatch(setPromotionalCodes(promoCodes));
            showAlertMessage('Código promocional guardado exitosamente');
            modalSavePromotionalCode.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };
    const onEditPromotionalCode = async (promotionalCode: PromotionalCode) => {
        setCurrentPromotionalCode(promotionalCode);
        modalSavePromotionalCode.toggle();
    };

    const onDeletePromotionalCode = async (promotionalCode: PromotionalCode) => {
        showLoader();
        try {
            if (promotionalCode.id) {
                await promotionalCodeService.deletePromotionalCode(promotionalCode.id);
                const promoCodes = [...promotionalCodes];
                const index = promoCodes.findIndex((p) => p.id === promotionalCode.id);
                if (index !== -1) {
                    promoCodes.splice(index, 1);
                }
                dispatch(setPromotionalCodes(promoCodes));
                //await getData();
                showAlertMessage('Código promocional eliminado exitosamente');
            }
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onToggleStatus = async (promotionalCode: PromotionalCode) => {
        showLoader();
        try {
            if (promotionalCode.id) {
                await promotionalCodeService.toggleStatus(promotionalCode.id);
                const promoCodes = [...promotionalCodes];
                const index = promoCodes.findIndex((p) => p.id === promotionalCode.id);
                if (index !== -1) {
                    promoCodes[index] = {
                        ...promoCodes[index],
                        status: !promotionalCode.status
                    };
                }
                //await getData();
                dispatch(setPromotionalCodes(promoCodes));
                showAlertMessage('Estatus del código promocional actualizado exitosamente');
            }
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        dataGridRef.current.setQuickFilterValues([filters.search]);
    }, [filters.search]);

    const columns = useMemo<GridColDef<PromotionalCode>[]>(
        () => [
            {
                field: 'code',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Código
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.code}</Typography>
            },
            {
                field: 'limit',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Limite
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.noLimit ? 'Sin limite' : row.usageLimit}</Typography>
            },
            {
                field: 'usage',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Usos
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.usage}</Typography>
            },
            {
                field: 'usageAmount',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Cantidad usada
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$ ${row.usageAmount?.toFixed(2)} MXN`}</Typography>
            },
            {
                field: 'actions',
                flex: 1 / 3,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Actions
                    </Typography>
                ),
                renderCell: ({ row }) => [
                    <Stack direction="row" alignItems="center" spacing={1} key="actions">
                        {/* El admin de sucursal ve los de "Todas" pero no los toca (F6). */}
                        <IOSSwitch
                            checked={row.status}
                            disabled={!canManagePromoCode(getCurrentPermissionSubject(), row)}
                            onChange={() => onToggleStatus(row)}
                        />
                        {/* La bitácora del código (M5): un descuento directo sobre
                            la venta, que hasta el 2026-09-11 se podía cambiar sin
                            que quedara rastro de quién ni de cuánto era antes. */}
                        <Tooltip title="Ver actividad de este código">
                            <IconButton
                                onClick={() =>
                                    dispatch(
                                        setActivityDrawer({
                                            isOpen: true,
                                            section: 'promotionalCode',
                                            sectionId: row.id
                                        })
                                    )
                                }
                            >
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                        {canManagePromoCode(getCurrentPermissionSubject(), row) && (
                            <IconButton onClick={() => onDeletePromotionalCode(row)}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Stack>
                ]
            }
        ],
        []
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} boxShadow={4} borderRadius={4} p={4} height="100%">
                <Stack>
                    <Grid container spacing={2} justifyContent="space-between">
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Buscar código"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <CustomButton
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={onOpenModalSavePromotionalCode}
                                >
                                    Nuevo descuento
                                </CustomButton>
                                <IconButton onClick={getData}>
                                    <ReplayIcon />
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
                <DataGrid
                    apiRef={dataGridRef}
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                    sx={{ borderWidth: 0 }}
                    rows={(promotionalCodes || []).filter((code) => isPromoCodeVisible(getCurrentPermissionSubject(), code))}
                    columns={columns}
                    onCellDoubleClick={(cel) => {
                        const code = cel.row as PromotionalCode;
                        if (canManagePromoCode(getCurrentPermissionSubject(), code)) void onEditPromotionalCode(code);
                    }}
                />
                <ModalSavePromotionalCode
                    modalSavePromotionalCode={modalSavePromotionalCode}
                    promotionalCode={currentPromotionalCode}
                    onSavePromotionalCode={onSavePromotionalCode}
                />
            </Stack>
        </Box>
    );
};
export default ListPromotionalCodesScreen;
